import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Moon, Play, Square, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { omnimizerKeys } from "../api";
import {
  availableCommands,
  errorMessage,
  gpuCapacityError,
  isTransitional,
  lifecycleApi,
  runCommand,
  transitionStep,
  type GpuCapacityReport,
  type LifecycleCommand,
} from "../lifecycle";
import type { OmnimizerDeployment } from "../types";

const ICONS: Record<LifecycleCommand, LucideIcon> = {
  start: Play,
  stop: Square,
  sleep: Moon,
  cancel: X,
  switch: Play,
};

type Pending = { kind: "stop" } | { kind: "switch"; report: GpuCapacityReport };

/**
 * Start / Stop / Sleep / Cancel start for a Dynamo deployment. When the GPU is taken,
 * offers a one-click switch: the API sleeps (or stops) the holders, then wakes this model.
 * `onChanged` lets non-react-query pages (legacy list) refresh while a transition runs.
 */
export function LifecycleActions({
  deployment,
  onChanged,
  className,
}: {
  deployment: OmnimizerDeployment;
  onChanged?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [error, setError] = useState("");
  const [requested, setRequested] = useState("");
  const statusKey = `${deployment.status}/${deployment.current_step}`;
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [watching, setWatching] = useState(false);
  const commands = availableCommands(deployment);
  const step = transitionStep(deployment);
  const transitional = isTransitional(deployment);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: omnimizerKeys.deployments });
    onChanged?.();
  };

  // Poll while the agent confirms the transition; stop once the row reaches a final state.
  useEffect(() => {
    if (!watching) return;
    if (!transitional) {
      return;
    }
    const timer = window.setInterval(refresh, 2000);
    const stop = window.setTimeout(() => setWatching(false), 300_000);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watching, transitional]);

  async function execute(command: LifecycleCommand) {
    setBusy(true); setError("");
    try {
      if (command === "switch") {
        const { data } = await lifecycleApi.switchTo(deployment.model_id);
        toast.success(
          t("omnimizer.lifecycle.switching", {
            name: deployment.name,
            released: data.released.map((r) => r.name).join(", ") || "—",
          }),
        );
      } else {
        await runCommand(deployment, command);
        toast.success(t(`omnimizer.lifecycle.requested.${command}`, { name: deployment.name }));
      }
      if (!mounted.current) return;
      setRequested(statusKey);
      setPending(null);
      setWatching(true);
      refresh();
    } catch (error) {
      const report = gpuCapacityError(error);
      if (report && command === "start") setPending({ kind: "switch", report });
      else {
        const message = errorMessage(error);
        const visible = message === "GPU hours quota exceeded for the current billing period" ? t("omnimizer.lifecycle.quotaExceeded") : message;
        setError(visible); toast.error(visible);
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  function onCommand(command: LifecycleCommand) {
    if (command === "stop") setPending({ kind: "stop" });
    else void execute(command);
  }

  if (!commands.length && !step) return null;

  const holders = pending?.kind === "switch" ? pending.report.holders : [];
  const othersBlock = pending?.kind === "switch" && pending.report.code === "gpu_held_by_others";
  const sleepers = holders.filter((h) => h.sleep_capable && h.status === "SUCCESS");

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {step && (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
          <Loader2 className="size-3.5 animate-spin motion-reduce:animate-none" aria-hidden />
          {t(`omnimizer.lifecycle.steps.${step}`)}
        </span>
      )}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {step === "pausing" && !commands.length && <Button size="sm" disabled>{t("omnimizer.lifecycle.stopping")}</Button>}
      {commands.map((command, index) => {
        const Icon = ICONS[command];
        return (
          <Button
            key={command}
            type="button"
            size="sm"
            variant={index === 0 && command === "start" ? "default" : command === "cancel" ? "outline" : "ghost"}
            disabled={busy || requested === statusKey}
            onClick={() => onCommand(command)}
            title={t(`omnimizer.lifecycle.hints.${command}`)}
          >
            {busy && index === 0 ? <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden /> : <Icon aria-hidden />}
            {t(`omnimizer.lifecycle.actions.${command}`)}
          </Button>
        );
      })}

      <AlertDialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          {pending?.kind === "stop" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("omnimizer.lifecycle.stopTitle", { name: deployment.name })}</AlertDialogTitle>
                <AlertDialogDescription>{t("omnimizer.lifecycle.stopBody")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("omnimizer.lifecycle.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={() => void execute("stop")}>{t("omnimizer.lifecycle.confirmStop")}</AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {pending?.kind === "switch" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("omnimizer.lifecycle.busyTitle")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {othersBlock
                    ? t("omnimizer.lifecycle.busyOthers")
                    : t("omnimizer.lifecycle.busyBody", { name: deployment.name })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <ul className="flex flex-col gap-1.5 text-sm">
                {holders.map((h) => (
                  <li key={h.model_id} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                    <span className="min-w-0 truncate font-medium">{h.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {h.sleep_capable && h.status === "SUCCESS"
                        ? t("omnimizer.lifecycle.willSleep")
                        : t("omnimizer.lifecycle.willStop")}
                    </span>
                  </li>
                ))}
              </ul>
              {!othersBlock && (
                <p className="text-xs text-muted-foreground">
                  {sleepers.length === holders.length
                    ? t("omnimizer.lifecycle.hotNote")
                    : t("omnimizer.lifecycle.coldNote")}
                </p>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel>{t("omnimizer.lifecycle.cancel")}</AlertDialogCancel>
                {!othersBlock && (
                  <AlertDialogAction onClick={() => void execute("switch")}>
                    {t("omnimizer.lifecycle.switchAction")}
                  </AlertDialogAction>
                )}
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
